import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Calendar, CheckSquare, Plus, Share2 } from "lucide-react";
import { api, shareText } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { Card, ChipRow, initialsOf, Overline } from "@/components/app/Primitives";
import { PriorityBadge, StatusBadge } from "@/components/app/Badges";
import { WhatsAppIcon } from "@/components/app/Layout";
import { CenterSpinner } from "@/components/app/Spinner";

const FILTERS = [
  { key: "all", label: "All" }, { key: "mine", label: "Mine" }, { key: "pending", label: "Pending" },
  { key: "in_progress", label: "In Progress" }, { key: "waiting_approval", label: "Approvals" },
  { key: "critical", label: "Critical" }, { key: "high", label: "High" }, { key: "completed", label: "Completed" },
  { key: "terminated", label: "Terminated" }, { key: "archived", label: "Archived" },
];
const TAB_MAP = { approvals: "waiting_approval", pending: "pending", in_progress: "in_progress", completed: "completed" };

export default function Tasks() {
  const { activeWorkspace } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const [filter, setFilter] = useState(() => TAB_MAP[params.get("tab")] || "all");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const query = useMemo(() => {
    if (!activeWorkspace) return null;
    const q = { workspace_id: activeWorkspace.id };
    if (filter === "mine") q.mine = true;
    else if (filter === "archived") q.archived = true;
    else if (filter === "completed") q.status = "approved";
    else if (["pending", "in_progress", "waiting_approval", "terminated"].includes(filter)) q.status = filter;
    else if (["critical", "high"].includes(filter)) q.priority = filter;
    return q;
  }, [activeWorkspace, filter]);

  const load = useCallback(async () => {
    if (!query) return;
    setLoading(true);
    try { setTasks(await api("/tasks", { query })); } catch (e) { console.log("tasks load", e); } finally { setLoading(false); }
  }, [query]);
  useEffect(() => { load(); }, [load]);

  const shareList = async (kind) => {
    if (!activeWorkspace) return;
    try {
      const list = await api("/tasks", { query: { workspace_id: activeWorkspace.id } });
      const filtered = list.filter((t) => (kind === "completed" ? ["completed", "approved"].includes(t.status) : !["completed", "approved", "cancelled"].includes(t.status)));
      if (filtered.length === 0) return toast.show(`No ${kind} tasks to share`, "info");
      const header = kind === "completed" ? `✅ Completed tasks — ${activeWorkspace.name}` : `📋 Open tasks — ${activeWorkspace.name}`;
      const lines = filtered.slice(0, 40).map((t, i) => `${i + 1}. ${t.title}  [${t.priority}]${t.deadline ? ` · due ${new Date(t.deadline).toLocaleDateString()}` : ""}`);
      const message = [header, "", ...lines, "", "Shared via Assignoo"].join("\n");
      if (kind === "pending") {
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener");
        return;
      }
      const r = await shareText({ message, title: header });
      if (r === "copied") toast.show("Task list copied to clipboard", "success");
    } catch (e) { toast.show(e instanceof Error ? e.message : "Share failed", "error"); }
  };

  return (
    <div className="page-in pt-4 md:pt-8" data-testid="tasks-page">
      <div className="flex items-center gap-2 px-4 pt-2">
        <div className="flex-1"><Overline>Workspace</Overline><h1 className="text-[28px] font-extrabold tracking-tight text-a-text">Tasks</h1></div>
        <button onClick={() => shareList("completed")} title="Share completed tasks" data-testid="tasks-share-completed" className="flex h-10 w-10 items-center justify-center rounded-xl border border-a-border bg-a-surface text-a-text hover:border-a-primary/50"><Share2 size={18} /></button>
        <button onClick={() => shareList("pending")} title="Share open tasks on WhatsApp" data-testid="tasks-share-whatsapp" className="flex h-10 w-10 items-center justify-center rounded-xl border border-a-border bg-a-surface hover:border-a-primary/50"><WhatsAppIcon size={18} color="rgb(var(--a-success))" /></button>
        <button onClick={() => navigate("/task/new")} data-testid="tasks-new-button" className="btn-press flex h-10 w-10 items-center justify-center rounded-xl bg-a-primary text-white hover:brightness-110"><Plus size={20} /></button>
      </div>
      <ChipRow options={FILTERS} value={filter} onChange={setFilter} testID="tasks-filter" className="px-4" />
      <div className="space-y-2.5 px-4 pt-2">
        {loading && tasks.length === 0 ? <CenterSpinner /> : tasks.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center gap-2 py-6 text-center">
              <CheckSquare size={36} className="text-a-muted" />
              <p className="font-bold text-a-text">No tasks in this view</p>
              <p className="text-a-muted">Try another filter or create a new task.</p>
            </div>
          </Card>
        ) : tasks.map((item) => (
          <Card key={item.id} onClick={() => navigate(`/task/${item.id}`)} testID={`task-card-${item.id}`}>
            <div className="space-y-2">
              <div className="flex justify-between gap-2"><p className="line-clamp-2 flex-1 text-[15px] font-bold text-a-text">{item.title}</p><PriorityBadge priority={item.priority} /></div>
              {item.description ? <p className="line-clamp-2 text-[13px] text-a-muted">{item.description}</p> : null}
              <div className="flex items-center justify-between">
                <StatusBadge status={item.status} />
                <div className="flex items-center gap-2 text-[11px] text-a-muted">
                  {item.deadline ? <span className="flex items-center gap-1"><Calendar size={12} />{new Date(item.deadline).toLocaleDateString()}</span> : null}
                  <span>{item.progress ?? 0}%</span>
                </div>
              </div>
              {item.assignees?.length ? (
                <div className="flex items-center">
                  {item.assignees.slice(0, 3).map((a, i) => (
                    <span key={a.id} className={`flex h-6 w-6 items-center justify-center rounded-full border-2 border-a-surface bg-a-dim text-[10px] font-bold text-a-primary ${i ? "-ml-2" : ""}`}>{initialsOf(a.full_name)}</span>
                  ))}
                  {item.assignees.length > 3 ? <span className="ml-1.5 text-[11px] text-a-muted">+{item.assignees.length - 3}</span> : null}
                </div>
              ) : null}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
